import classNames from "classnames";
import { useCallback, useEffect, useRef, useState } from "react";

import styles from "./accordion.module.scss";

interface HeaderInterface {
  isOpen: boolean;
  onToggleAccordion: () => void;
}

interface AccordionInterface {
  className?: string;
  header: ({ isOpen, onToggleAccordion }: HeaderInterface) => JSX.Element;
  children: JSX.Element;
  overflowedChildren?: JSX.Element;
  isOpenDefault?: boolean;
  isScroll?: boolean;
  accordionScrollWrapperId?: string;
}

const Accordion = ({
  className,
  header,
  children,
  overflowedChildren,
  isOpenDefault,
  isScroll,
  accordionScrollWrapperId
}: AccordionInterface) => {
  const contentRef = useRef(null);

  const [isOpen, setIsOpen] = useState(isOpenDefault);
  const [maxHeight, setMaxHeight] = useState(isOpenDefault ? "none" : "0px");
  const [isNoAnimation, setIsNoAnimation] = useState(isOpenDefault);

  const onToggleAccordion = useCallback(() => {
    setIsOpen((prevVal) => !prevVal);
  }, []);

  const updateMaxHeight = useCallback(() => {
    const content = contentRef.current;
    if (content) {
      const bottomPadding = 20;
      const pageViewHeight =
        window.innerHeight -
        content.getBoundingClientRect().top -
        bottomPadding;
      const contentHeight = content.scrollHeight;

      if (isScroll) {
        const maxHeight =
          contentHeight > pageViewHeight ? pageViewHeight : contentHeight;

        setMaxHeight(`${maxHeight}px`);
      } else {
        setMaxHeight(`${contentHeight}px`);
      }
    }
  }, [isScroll]);

  useEffect(() => {
    updateMaxHeight();

    window.addEventListener("resize", updateMaxHeight);

    return () => {
      window.removeEventListener("resize", updateMaxHeight);
    };
  }, [updateMaxHeight, children]);

  useEffect(() => {
    const content = contentRef.current;
    const resizeObserver = new ResizeObserver(() => {
      updateMaxHeight();
    });

    if (content) {
      resizeObserver.observe(content);
    }

    return () => {
      if (content) {
        resizeObserver.unobserve(content);
      }
    };
  }, [isOpen, children, updateMaxHeight]);

  useEffect(() => {
    if (isOpen) {
      updateMaxHeight();
      setIsNoAnimation(false);
    }
  }, [isOpen, children, updateMaxHeight]);

  const handleInnerResize = useCallback(() => {
    updateMaxHeight();
  }, [updateMaxHeight]);

  return (
    <div className={classNames(styles["wrapper"], className)}>
      <div className={styles["wrapper__header"]}>
        {header({ isOpen, onToggleAccordion })}
      </div>
      {overflowedChildren && isOpen && (
        <div style={{ position: "absolute", top: 0 }}>{overflowedChildren}</div>
      )}
      <div
        id={accordionScrollWrapperId}
        className={classNames(styles["wrapper__body"], {
          [styles["wrapper__body--scroll"]]: isScroll,
          [styles["wrapper__body--no-animation"]]: isNoAnimation
        })}
        style={{ maxHeight: isOpen ? maxHeight : "0px" }}
        ref={contentRef}
        onTransitionEnd={handleInnerResize}
      >
        <div className={styles["wrapper__content"]}>{children}</div>
      </div>
    </div>
  );
};

export default Accordion;
